import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Input } from '@appcharge/shared-ui';
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { EToasterStatus, showToast } from 'utils/showToast';

import CustomizedSwitch from 'components/SwitchButton/SwitchButton';

import { IntegrationSettingsData } from '../../Settings.types';
import { toastMessages } from '../DeveloperSettings.constants';
import { appsFlyerSchema } from '../DeveloperSettings.schemas';
import { ESavingForm, ExtendedIntegrationData } from '../DeveloperSettings.types';

import { SettingsCard } from './SettingsCard';

interface IAppsFlyerFormValues {
  appsFlyerIntegrationOn: boolean;
  appsFlyerS2SToken: string;
  appsFlyerApplicationID: string;
}

interface IAppsFlyerSectionProps {
  integrationData: ExtendedIntegrationData | undefined;
  updateIntegration: UseMutationResult<unknown, unknown, Partial<IntegrationSettingsData>>;
  getIntegration: UseQueryResult<IntegrationSettingsData, Error>;
  savingForm: ESavingForm;
  setSavingForm: (form: ESavingForm) => void;
  isProfilesEnabled: boolean;
}

export const AppsFlyerSection: React.FC<IAppsFlyerSectionProps> = ({
  integrationData,
  updateIntegration,
  getIntegration,
  savingForm,
  setSavingForm,
  isProfilesEnabled
}) => {
  const [isEnabled, setIsEnabled] = useState(false);

  const initialValues = useMemo((): IAppsFlyerFormValues => {
    const appsFlyerData = integrationData?.appsFlyerIntegration;
    return {
      appsFlyerIntegrationOn: appsFlyerData?.appsFlyerIntegrationOn || false,
      appsFlyerS2SToken: appsFlyerData?.appsFlyerS2SToken || '',
      appsFlyerApplicationID: appsFlyerData?.appsFlyerApplicationID || ''
    };
  }, [integrationData?.appsFlyerIntegration]);

  useEffect(() => {
    setIsEnabled(initialValues.appsFlyerIntegrationOn);
  }, [initialValues.appsFlyerIntegrationOn]);

  const handleSaveAppsFlyerSettings = useCallback(
    (values: IAppsFlyerFormValues) => {
      setSavingForm(ESavingForm.APPSFLYER);
      updateIntegration.mutate(
        {
          appsFlyerIntegration: {
            appsFlyerIntegrationOn: values.appsFlyerIntegrationOn,
            appsFlyerS2SToken: values.appsFlyerS2SToken,
            appsFlyerApplicationID: values.appsFlyerApplicationID
          }
        },
        {
          onSuccess: () => {
            showToast({
              message: toastMessages.appsFlyerSuccess,
              status: EToasterStatus.SUCCESS
            });
            getIntegration.refetch();
            setSavingForm(ESavingForm.NONE);
          },
          onError: () => {
            showToast({
              message: toastMessages.appsFlyerError,
              status: EToasterStatus.ERROR
            });
            setSavingForm(ESavingForm.NONE);
          }
        }
      );
    },
    [updateIntegration, getIntegration, setSavingForm]
  );

  const form = useFormik<IAppsFlyerFormValues>({
    initialValues,
    validationSchema: appsFlyerSchema,
    enableReinitialize: true,
    onSubmit: handleSaveAppsFlyerSettings
  });

  const handleToggleAppsFlyer = useCallback(() => {
    const newValue = !isEnabled;
    setIsEnabled(newValue);
    form.setFieldValue('appsFlyerIntegrationOn', newValue);
    if (!newValue) {
      form.setFieldValue('appsFlyerS2SToken', '');
      form.setFieldValue('appsFlyerApplicationID', '');
    }
  }, [isEnabled, form]);

  const isSaveDisabled = !form.dirty || savingForm !== ESavingForm.NONE;
  const isLoading = savingForm === ESavingForm.APPSFLYER;

  return (
    <SettingsCard
      title="AppsFlyer Integration"
      description="Connect your AppsFlyer account to track in-app events and attribution data."
      onSave={form.submitForm}
      isSaveDisabled={isSaveDisabled}
      isLoading={isLoading}
      saveButtonDataTestId="apps-flyer-save"
    >
      <div className={isProfilesEnabled ? 'opacity-50 pointer-events-none' : ''}>
        <div className="mb-6 flex items-center gap-3">
          <CustomizedSwitch
            status={isEnabled}
            texts={[]}
            functions={[handleToggleAppsFlyer, handleToggleAppsFlyer]}
          />
          <div>
            <p className="text-sm font-medium text-gray-900">
              Enable AppsFlyer Integration
            </p>
            <p className="text-[13px] text-gray-500">
              Track user acquisition and in-app events with AppsFlyer analytics.
            </p>
          </div>
        </div>

        {isEnabled && (
          <div className="grid grid-cols-2 gap-4 border-t border-gray-100 pt-6">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                AppsFlyer S2S Token
                <span className="text-red-500 ml-1">*</span>
              </label>
              <Input
                name="appsFlyerS2SToken"
                value={form.values.appsFlyerS2SToken}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                placeholder="Enter S2S token"
              />
              {form.touched.appsFlyerS2SToken && form.errors.appsFlyerS2SToken && (
                <span className="text-xs text-red-500">
                  {form.errors.appsFlyerS2SToken}
                </span>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Application ID
                <span className="text-red-500 ml-1">*</span>
              </label>
              <Input
                name="appsFlyerApplicationID"
                value={form.values.appsFlyerApplicationID}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                placeholder="Enter Application ID"
              />
              {form.touched.appsFlyerApplicationID && form.errors.appsFlyerApplicationID && (
                <span className="text-xs text-red-500">
                  {form.errors.appsFlyerApplicationID}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </SettingsCard>
  );
};
